import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Typography,
  CircularProgress,
  TextField,
  Grid,
  Button,
  Paper,
  Box,
} from "@mui/material";
import dayjs from "dayjs";
import {
  getProcurementDetail,
  updateProcurement,
} from "../../api/biomassProcurement";
import { useTranslation } from "react-i18next";
import { useAlert } from "../../components/Alert";
import CustomAppBar from "../../components/AppBar";
import { getUserBasicDetailFromAuth } from "../../utils/common";
import { getProcurementEstimatedPayout } from "../../utils/biomassProcurement";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";

const InspectableProcurement = ({ procurement }) => {
  const { t } = useTranslation();
  if (!procurement) {
    return null;
  }
  return (
    <Paper
      sx={{
        padding: 2,
        maxWidth: 600,
        margin: "auto",
        paddingBottom: 4,
        mt: 2,
      }}
    >
      <Typography variant="h6">{t("Procurement Details")}</Typography>
      <Typography>
        <strong>{t("Farmer Name")}:</strong> {procurement.farmer_name}
      </Typography>
      <Typography>
        <strong>{t("Mobile")}:</strong> {procurement.mobile}
      </Typography>
      <Typography>
        <strong>{t("Crop Name")}:</strong> {procurement.crop_name}
      </Typography>
      <Typography>
        <strong>{t("Acreage")}:</strong> {procurement.acreage}
      </Typography>
      <Typography>
        <strong>{t("Address")}:</strong> {procurement.address_line1},{" "}
        {procurement.village}, {procurement.postoffice_name},{" "}
        {procurement.district_name}, {procurement.state_name},{" "}
        {procurement.pincode}
      </Typography>
    </Paper>
  );
};

const ProcurementInspection = () => {
  const { app_user_id } = getUserBasicDetailFromAuth();
  const { id } = useParams();
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [procurement, setProcurement] = useState(null);

  const [formData, setFormData] = useState({
    quantity_kg: "",
    estimated_payout: "",
    inspected_on: dayjs(),
    inspected_by: app_user_id,
    inspection_remarks: "",
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    if (e.target.name === "quantity_kg") {
      const estimated_payout = getProcurementEstimatedPayout(
        e.target.value,
        procurement.transportation_by
      );
      setFormData({
        ...formData,
        [e.target.name]: e.target.value,
        estimated_payout: estimated_payout,
      });
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  const handleDateChange = (value) => {
    setFormData({ ...formData, inspected_on: value });
  };

  const fetchProcurementDetails = async () => {
    try {
      const response = await getProcurementDetail(id);
      setProcurement(response);
      setFormData({
        ...formData,
        quantity_kg: response.quantity_kg,
        estimated_payout: response.estimated_payout,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching procurement details:", error);
      showAlert("error", "Failed to fetch procurement details");
      setLoading(false);
    }
  };

  const handleSubmit = async (status) => {
    const errors = {};
    if (!formData.quantity_kg) {
      errors.quantity_kg = "Quantity is required";
    }
    if (!formData.inspected_on) {
      errors.inspected_on = "Inspected date time is required";
    }

    setErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    try {
      const request_data = {
        quantity_kg: formData.quantity_kg,
        estimated_payout: formData.estimated_payout || 0,
        inspected_on: formData.inspected_on,
        status: status,
        inspected_by: formData.inspected_by || app_user_id,
        inspection_remarks: formData.inspection_remarks || "",
      };
      const response = await updateProcurement(id, request_data);
      console.log("Procurement updated:", response);
      const alertMessage = status === "ACCEPTED" ? "Accepted" : "Rejected";
      showAlert("success", t(`Procurement ${alertMessage} successfully`));
      if (status === "ACCEPTED") {
        navigate(`/procurement/pickup-request/${id}`);
      } else {
        navigate("/procurement-list");
      }
    } catch (error) {
      console.error("Error updating inspection:", error);
      showAlert("error", "Failed to update inspection");
    }
  };

  useEffect(() => {
    fetchProcurementDetails();
  }, []);

  return (
    <>
      <CustomAppBar />
      {loading ? (
        <CircularProgress />
      ) : procurement ? (
        <Box sx={{ padding: 2 }}>
          <InspectableProcurement procurement={procurement} />
          <Typography variant="h6" marginBottom={1} marginTop={2}>
            {t("Inspection Details")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="quantity_kg"
                value={formData.quantity_kg}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                label={t("Quantity (kg)")}
                error={!!errors.quantity_kg}
                helperText={errors.quantity_kg}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="estimated_payout"
                value={formData.estimated_payout}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                label={t("Estimated Payout")}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DateTimePicker"]}>
                  <DateTimePicker
                    label={t("Inspected on")}
                    name="inspected_on"
                    value={formData.inspected_on}
                    onChange={handleDateChange}
                    error={!!errors.inspected_on}
                    helperText={errors.inspected_on}
                    required
                    fullWidth
                  />
                </DemoContainer>
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="inspection_remarks"
                value={formData.inspection_remarks}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                label={t("Inspection Remarks")}
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={6}>
              <Button
                variant="contained"
                color="error"
                onClick={async () => await handleSubmit("REJECTED")}
                fullWidth
              >
                {t("Reject")}
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => await handleSubmit("ACCEPTED")}
                fullWidth
              >
                {t("Accept")}
              </Button>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Typography variant="h6">{t("Procurement not found")}</Typography>
      )}
    </>
  );
};

export default ProcurementInspection;
